import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { ArrowDown, ArrowUp, Minus, RefreshCw, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { PriceAdjustment, PriceAdjustmentProduct, purchasesService, SavePriceAdjustmentPayload } from '../../../services/purchases.service'
import { Button } from '../../ui/button'
import { Checkbox } from '../../ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog'
import { DiscreteLoader } from '../../ui/discrete-loader'
import { Input } from '../../ui/input'

interface PriceAdjustmentModalProps {
  showPriceAdjustmentModal: boolean
  setShowPriceAdjustmentModal: (show: boolean) => void
  selectedAdjustment: PriceAdjustment | null
}

const rounded = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100
const money = (value: number | null) => value === null ? 'Sin dato' : `$${value.toFixed(2)}`
const percentage = (value: number | null) => value === null ? 'Sin dato' : `${value.toFixed(2)}%`

export function PriceAdjustmentModal({
  showPriceAdjustmentModal,
  setShowPriceAdjustmentModal,
  selectedAdjustment,
}: PriceAdjustmentModalProps) {
  const queryClient = useQueryClient()
  const [products, setProducts] = useState<PriceAdjustmentProduct[]>([])
  const [keepPreviousPrice, setKeepPreviousPrice] = useState<Record<number, boolean>>({})

  const detailQuery = useQuery({
    queryKey: ['purchase-price-adjustment', selectedAdjustment?.purchaseNumericId],
    queryFn: () => purchasesService.getPriceAdjustment(selectedAdjustment!.purchaseNumericId),
    enabled: showPriceAdjustmentModal && Boolean(selectedAdjustment),
  })

  useEffect(() => {
    const detail = detailQuery.data?.data
    if (!detail) return

    setProducts(detail.products)
    setKeepPreviousPrice(Object.fromEntries(
      detail.products.map((product) => [product.id, product.canKeepPrevious && product.decision === 'kept']),
    ))
  }, [detailQuery.data])

  const mutation = useMutation({
    mutationFn: (payload: SavePriceAdjustmentPayload) => {
      if (!selectedAdjustment) throw new Error('No hay un ajuste seleccionado.')
      return selectedAdjustment.status === 'updated'
        ? purchasesService.updatePriceAdjustment(selectedAdjustment.purchaseNumericId, payload)
        : purchasesService.applyPriceAdjustment(selectedAdjustment.purchaseNumericId, payload)
    },
    onSuccess: async () => {
      toast.success(selectedAdjustment?.status === 'updated' ? 'Ajuste de precios editado.' : 'Ajuste de precios aplicado.')
      setShowPriceAdjustmentModal(false)
      setProducts([])
      setKeepPreviousPrice({})
      await queryClient.invalidateQueries({ queryKey: ['purchase-price-adjustments'] })
      await queryClient.invalidateQueries({ queryKey: ['purchase-price-adjustment', selectedAdjustment?.purchaseNumericId] })
    },
    onError: (error) => {
      const message = isAxiosError<{ message?: string }>(error) ? error.response?.data?.message : undefined
      toast.error(message || 'No fue posible aplicar los ajustes de precio.')
    },
  })

  const detail = detailQuery.data?.data ?? selectedAdjustment
  const hasUnavailableKeep = products.some((product) => keepPreviousPrice[product.id] && !product.canKeepPrevious)
  const hasBlockingProduct = products.some((product) => !keepPreviousPrice[product.id] && !product.canAdjust)
  const hasInvalidProduct = products.some((product) => !keepPreviousPrice[product.id] && (
    product.newSalePrice === null
    || product.newMargin === null
    || !Number.isFinite(product.newSalePrice)
    || !Number.isFinite(product.newMargin)
    || product.newSalePrice <= 0
    || product.newMargin <= -100
  ))

  const updateFromFinalPrice = (index: number, finalPrice: number | null) => {
    const product = products[index]
    if (finalPrice === null || !Number.isFinite(finalPrice)) {
      const updated = [...products]
      updated[index] = { ...product, newSalePrice: null, newMargin: null, calculationSource: 'final_price' }
      setProducts(updated)
      return
    }

    const basePrice = finalPrice / (1 + product.taxPercentage / 100)
    const profit = product.newCost > 0 ? ((basePrice - product.newCost) / product.newCost) * 100 : 0
    const updated = [...products]
    updated[index] = {
      ...product,
      newSalePrice: finalPrice,
      newMargin: rounded(profit),
      calculationSource: 'final_price',
    }
    setProducts(updated)
  }

  const updateFromProfit = (index: number, profit: number | null) => {
    const product = products[index]
    if (profit === null || !Number.isFinite(profit)) {
      const updated = [...products]
      updated[index] = { ...product, newSalePrice: null, newMargin: null, calculationSource: 'profit_percentage' }
      setProducts(updated)
      return
    }

    const basePrice = product.newCost * (1 + profit / 100)
    const finalPrice = basePrice * (1 + product.taxPercentage / 100)
    const updated = [...products]
    updated[index] = {
      ...product,
      newSalePrice: rounded(finalPrice),
      newMargin: profit,
      calculationSource: 'profit_percentage',
    }
    setProducts(updated)
  }

  const submit = () => {
    if (hasUnavailableKeep || hasBlockingProduct || hasInvalidProduct) return

    const payload: SavePriceAdjustmentPayload = {
      items: products.map((product) => keepPreviousPrice[product.id]
        ? { product_id: product.id, decision: 'keep' }
        : {
            product_id: product.id,
            decision: 'apply',
            calculation_source: product.calculationSource ?? 'profit_percentage',
            ...(product.calculationSource === 'final_price'
              ? { final_price: product.newSalePrice! }
              : { profit_percentage: product.newMargin! }),
          }),
    }
    mutation.mutate(payload)
  }

  return (
    <Dialog open={showPriceAdjustmentModal} onOpenChange={setShowPriceAdjustmentModal}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-7xl">
        <DialogHeader>
          <DialogTitle>Detalle de Ajuste de Precio</DialogTitle>
          <DialogDescription className="sr-only">Detalles de los productos con cambios de precio</DialogDescription>
        </DialogHeader>

        {detailQuery.isLoading ? (
          <div className="min-h-64"><DiscreteLoader text="Cargando detalle del ajuste..." /></div>
        ) : detailQuery.isError ? (
          <div className="flex min-h-56 flex-col items-center justify-center rounded-lg border border-red-200 bg-red-50 p-6 text-center">
            <p className="font-medium text-red-800">No fue posible cargar el detalle del ajuste</p>
            <Button variant="outline" className="mt-4" onClick={() => void detailQuery.refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" /> Reintentar
            </Button>
          </div>
        ) : detail && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white shadow-sm md:grid-cols-4 md:divide-x md:divide-y-0">
              <div className="flex flex-col p-4"><span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">ID</span><span className="text-sm font-semibold text-slate-900">{detail.id}</span></div>
              <div className="flex flex-col p-4"><span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Proveedor</span><span className="text-sm font-semibold text-slate-900">{detail.supplier}</span></div>
              <div className="flex flex-col p-4"><span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Fecha</span><span className="text-sm font-semibold text-slate-900">{detail.date}</span></div>
              <div className="flex flex-col p-4"><span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">ID Compra</span><span className="text-sm font-semibold text-slate-900">{detail.purchaseId}</span></div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    {['Mantener Anterior', 'Producto', 'Cant.', 'Costo Anterior', 'Costo Nuevo', 'Cambio', 'Precio Venta Anterior', 'Utilidad % Anterior', 'Precio Venta Nuevo', 'Utilidad % Nuevo', 'Lote'].map((heading) => (
                      <th key={heading} className="px-4 py-4 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">{heading}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {products.map((product, index) => {
                    const keepPrevious = keepPreviousPrice[product.id] || false
                    const displaySalePrice = keepPrevious ? product.previousSalePrice : product.newSalePrice
                    const displayMargin = keepPrevious ? product.previousMargin : product.newMargin

                    return (
                      <tr key={product.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="flex flex-col items-center justify-center gap-1">
                            <Checkbox
                              aria-label={`Mantener precio anterior ${product.name}`}
                              checked={keepPrevious}
                              onCheckedChange={(checked) => setKeepPreviousPrice({ ...keepPreviousPrice, [product.id]: checked === true })}
                              disabled={!product.canKeepPrevious}
                            />
                            {product.keepBlockingReason && <p className="max-w-32 text-center text-xs text-amber-700">{product.keepBlockingReason}</p>}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <p className="font-medium text-slate-900">
                            {product.name}
                          </p>
                          {product.barcode && (
                            <p className="mt-1 font-mono text-xs text-slate-500">
                              {product.barcode}
                            </p>
                          )}
                          {product.blockingReason && (
                            <p className="mt-1 max-w-52 text-xs text-red-600">
                              {product.blockingReason}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3"><span className="font-medium text-slate-900">{product.quantity}</span></td>
                        <td className="px-4 py-3"><span className="text-slate-700">{money(product.previousCost)}</span></td>
                        <td className="px-4 py-3"><span className="font-semibold text-slate-900">${product.newCost.toFixed(2)}</span></td>
                        <td className="px-4 py-3">
                          {product.costChange === null ? (
                            <span className="text-slate-500">—</span>
                          ) : product.costChange === 0 ? (
                            <div className="flex items-center gap-1 text-slate-600"><Minus className="h-4 w-4" /><span className="text-sm font-medium">0%</span></div>
                          ) : product.costChange > 0 ? (
                            <div className="flex items-center gap-1 text-red-600"><ArrowUp className="h-4 w-4" /><span className="text-sm font-semibold">+{product.costChange.toFixed(2)}%</span></div>
                          ) : (
                            <div className="flex items-center gap-1 text-green-600"><ArrowDown className="h-4 w-4" /><span className="text-sm font-semibold">{product.costChange.toFixed(2)}%</span></div>
                          )}
                        </td>
                        <td className="px-4 py-3"><span className="text-slate-700">{money(product.previousSalePrice)}</span></td>
                        <td className="px-4 py-3"><span className="text-slate-700">{percentage(product.previousMargin)}</span></td>
                        <td className="px-4 py-3">
                          <Input
                            aria-label={`Precio Venta Nuevo ${product.name}`}
                            type="number" step="0.01" value={displaySalePrice ?? ''}
                            onChange={(event) => updateFromFinalPrice(index, event.target.value === '' ? null : Number(event.target.value))}
                            disabled={keepPrevious || !product.canAdjust}
                            className="h-9 w-24 text-sm bg-white"
                          />
                        </td>
                        <td className="px-4 py-4">
                          <Input
                            aria-label={`Utilidad Nueva ${product.name}`}
                            type="number" step="0.01" min="-99.99" value={displayMargin ?? ''}
                            onChange={(event) => updateFromProfit(index, event.target.value === '' ? null : Number(event.target.value))}
                            disabled={keepPrevious || !product.canAdjust}
                            className="h-9 w-20 text-sm bg-white"
                          />
                        </td>
                        <td className="px-4 py-4"><span className="text-xs text-slate-600">{product.lotNumber}</span></td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex gap-3">
              <Button onClick={submit} disabled={mutation.isPending || hasUnavailableKeep || hasBlockingProduct || hasInvalidProduct || products.length === 0} className="flex-1" size="lg">
                <RefreshCw className={`mr-2 h-5 w-5 ${mutation.isPending ? 'animate-spin' : ''}`} />
                {mutation.isPending ? 'Aplicando...' : 'Aplicar Ajustes de Precio'}
              </Button>
              <Button variant="outline" onClick={() => setShowPriceAdjustmentModal(false)} className="flex-1" size="lg">
                <X className="mr-2 h-5 w-5" /> Cerrar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
